import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import type {
    Scorecard,
    ScorecardGate,
    ScorecardCriterion,
    ScorecardBonus,
    ScorecardTierLabel,
} from "@/components/atomic-crm/types";

// ─── Local state shapes (id may be undefined for new rows) ────────────────────

type LocalGate = Omit<ScorecardGate, "id" | "scorecard_id"> & { _localId: string; id?: number };
type LocalCriterion = Omit<ScorecardCriterion, "id" | "scorecard_id"> & { _localId: string; id?: number };
type LocalBonus = Omit<ScorecardBonus, "id" | "scorecard_id"> & { _localId: string; id?: number };
type LocalTierLabel = Omit<ScorecardTierLabel, "id" | "scorecard_id"> & { _localId: string; id?: number };

let _counter = 0;
function uid() {
    return `new-${++_counter}`;
}

const OPERATORS: ScorecardGate["operator"][] = ["eq", "gte", "in", "exists"];

// ─── Small reusable row components ────────────────────────────────────────────

function FieldInput({
    label,
    value,
    onChange,
    placeholder,
    className,
}: {
    label?: string;
    value: string | number;
    onChange: (v: string) => void;
    placeholder?: string;
    className?: string;
}) {
    return (
        <div className={className}>
            {label && <label className="block text-xs text-muted-foreground mb-1">{label}</label>}
            <Input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
            />
        </div>
    );
}

// ─── Gate tab ─────────────────────────────────────────────────────────────────

function GateRow({
    gate,
    onChange,
    onDelete,
}: {
    gate: LocalGate;
    onChange: (updated: LocalGate) => void;
    onDelete: () => void;
}) {
    const set = (field: keyof LocalGate, val: any) => onChange({ ...gate, [field]: val });

    return (
        <div className="grid grid-cols-[1fr_1fr_1fr_120px_120px_auto] gap-2 items-end py-2 border-b last:border-b-0">
            <FieldInput
                label="Code"
                value={gate.code}
                onChange={(v) => set("code", v)}
                placeholder="GATE_B2B"
            />
            <FieldInput
                label="Label"
                value={gate.label}
                onChange={(v) => set("label", v)}
                placeholder="Business Model B2B"
            />
            <FieldInput
                label="Field Reference"
                value={gate.field_reference}
                onChange={(v) => set("field_reference", v)}
                placeholder="company.business_model"
            />
            <div>
                <label className="block text-xs text-muted-foreground mb-1">Operator</label>
                <Select
                    value={gate.operator}
                    onValueChange={(v) => set("operator", v as ScorecardGate["operator"])}
                >
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {OPERATORS.map((op) => (
                            <SelectItem key={op} value={op}>
                                {op}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <FieldInput
                label="Threshold"
                value={gate.threshold_value ?? ""}
                onChange={(v) => set("threshold_value", v)}
                placeholder="B2B"
            />
            <div className="pb-0.5">
                <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={onDelete}
                >
                    Remove
                </Button>
            </div>
        </div>
    );
}

// ─── Criterion / Bonus tab row ────────────────────────────────────────────────

type TierEntry = { value: any; points: number; label: string };

function TierEditor({
    tiers,
    onChange,
}: {
    tiers: TierEntry[];
    onChange: (t: TierEntry[]) => void;
}) {
    const addTier = () =>
        onChange([...tiers, { value: "", points: 0, label: "" }]);
    const removeTier = (i: number) =>
        onChange(tiers.filter((_, idx) => idx !== i));
    const setTier = (i: number, field: keyof TierEntry, val: any) =>
        onChange(
            tiers.map((t, idx) =>
                idx === i ? { ...t, [field]: field === "points" ? Number(val) : val } : t,
            ),
        );

    return (
        <div className="mt-2 space-y-1">
            <div className="grid grid-cols-[1fr_80px_1fr_auto] gap-1 text-xs text-muted-foreground mb-1">
                <span>Value</span>
                <span>Points</span>
                <span>Label</span>
                <span />
            </div>
            {tiers.map((t, i) => (
                <div key={i} className="grid grid-cols-[1fr_80px_1fr_auto] gap-1 items-center">
                    <Input
                        value={t.value}
                        onChange={(e) => setTier(i, "value", e.target.value)}
                        placeholder="B2B"
                        className="h-7 text-xs"
                    />
                    <Input
                        type="number"
                        value={t.points}
                        onChange={(e) => setTier(i, "points", e.target.value)}
                        className="h-7 text-xs"
                    />
                    <Input
                        value={t.label}
                        onChange={(e) => setTier(i, "label", e.target.value)}
                        placeholder="Full match"
                        className="h-7 text-xs"
                    />
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-destructive hover:text-destructive px-2"
                        onClick={() => removeTier(i)}
                    >
                        ×
                    </Button>
                </div>
            ))}
            <Button variant="outline" size="sm" className="mt-1 h-7 text-xs" onClick={addTier}>
                + Tier
            </Button>
        </div>
    );
}

function CriterionRow({
    item,
    onChange,
    onDelete,
}: {
    item: LocalCriterion | LocalBonus;
    onChange: (updated: LocalCriterion | LocalBonus) => void;
    onDelete: () => void;
}) {
    const set = (field: string, val: any) => onChange({ ...item, [field]: val } as any);
    const [expanded, setExpanded] = useState(false);

    return (
        <div className="border-b last:border-b-0 py-2">
            <div className="grid grid-cols-[1fr_1fr_1fr_80px_auto_auto] gap-2 items-end">
                <FieldInput
                    label="Code"
                    value={item.code}
                    onChange={(v) => set("code", v)}
                    placeholder="FUNCTION"
                />
                <FieldInput
                    label="Label"
                    value={item.label}
                    onChange={(v) => set("label", v)}
                    placeholder="Function / Role"
                />
                <FieldInput
                    label="Field Reference"
                    value={item.field_reference}
                    onChange={(v) => set("field_reference", v)}
                    placeholder="contact.function_role"
                />
                <FieldInput
                    label="Max pts"
                    value={item.max_points}
                    onChange={(v) => set("max_points", Number(v))}
                    placeholder="20"
                />
                <div className="pb-0.5">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setExpanded((x) => !x)}
                    >
                        {expanded ? "Hide Tiers" : `Tiers (${item.tiers.length})`}
                    </Button>
                </div>
                <div className="pb-0.5">
                    <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={onDelete}
                    >
                        Remove
                    </Button>
                </div>
            </div>
            {expanded && (
                <div className="mt-2 pl-4">
                    <TierEditor
                        tiers={item.tiers}
                        onChange={(t) => set("tiers", t)}
                    />
                </div>
            )}
        </div>
    );
}

// ─── Tier Label row ───────────────────────────────────────────────────────────

function TierLabelRow({
    item,
    onChange,
    onDelete,
}: {
    item: LocalTierLabel;
    onChange: (updated: LocalTierLabel) => void;
    onDelete: () => void;
}) {
    const set = (field: keyof LocalTierLabel, val: any) =>
        onChange({ ...item, [field]: val });

    return (
        <div className="grid grid-cols-[1fr_80px_80px_80px_80px_auto] gap-2 items-end py-2 border-b last:border-b-0">
            <FieldInput
                label="Name"
                value={item.name}
                onChange={(v) => set("name", v)}
                placeholder="Angel Target"
            />
            <FieldInput
                label="Min Score"
                value={item.min_score}
                onChange={(v) => set("min_score", Number(v))}
                placeholder="85"
            />
            <FieldInput
                label="Max Score"
                value={item.max_score}
                onChange={(v) => set("max_score", Number(v))}
                placeholder="100"
            />
            <div>
                <label className="block text-xs text-muted-foreground mb-1">Color</label>
                <input
                    type="color"
                    value={item.color || "#000000"}
                    onChange={(e) => set("color", e.target.value)}
                    className="h-9 w-full cursor-pointer rounded border bg-transparent p-0.5"
                />
            </div>
            <FieldInput
                label="Sort"
                value={item.sort_order}
                onChange={(v) => set("sort_order", Number(v))}
                placeholder="0"
            />
            <div className="pb-0.5">
                <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={onDelete}
                >
                    Remove
                </Button>
            </div>
        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ScorecardEdit() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isNew = id === "new" || id === undefined;

    // Scorecard header fields
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [bonusCap, setBonusCap] = useState(10);

    // Child records
    const [gates, setGates] = useState<LocalGate[]>([]);
    const [criteria, setCriteria] = useState<LocalCriterion[]>([]);
    const [bonuses, setBonuses] = useState<LocalBonus[]>([]);
    const [tierLabels, setTierLabels] = useState<LocalTierLabel[]>([]);

    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(!isNew);
    const [error, setError] = useState<string | null>(null);

    // Load existing scorecard
    useEffect(() => {
        if (isNew) return;
        const scorecardId = Number(id);

        Promise.all([
            supabase.from("scorecards").select("*").eq("id", scorecardId).single(),
            supabase.from("scorecard_gates").select("*").eq("scorecard_id", scorecardId).order("sort_order"),
            supabase.from("scorecard_criteria").select("*").eq("scorecard_id", scorecardId).order("sort_order"),
            supabase.from("scorecard_bonuses").select("*").eq("scorecard_id", scorecardId).order("sort_order"),
            supabase.from("scorecard_tier_labels").select("*").eq("scorecard_id", scorecardId).order("sort_order"),
        ]).then(([sc, g, c, b, t]) => {
            if (sc.data) {
                setName(sc.data.name);
                setDescription(sc.data.description ?? "");
                setBonusCap(sc.data.bonus_cap);
            }
            if (g.data) setGates(g.data.map((x: ScorecardGate) => ({ ...x, _localId: String(x.id) })));
            if (c.data) setCriteria(c.data.map((x: ScorecardCriterion) => ({ ...x, _localId: String(x.id) })));
            if (b.data) setBonuses(b.data.map((x: ScorecardBonus) => ({ ...x, _localId: String(x.id) })));
            if (t.data) setTierLabels(t.data.map((x: ScorecardTierLabel) => ({ ...x, _localId: String(x.id) })));
            setLoading(false);
        });
    }, [id, isNew]);

    // Save
    const handleSave = async () => {
        setSaving(true);
        setError(null);

        try {
            let scorecardId: number;

            if (isNew) {
                const { data, error: err } = await supabase
                    .from("scorecards")
                    .insert({ name, description, bonus_cap: bonusCap, is_active: false })
                    .select("id")
                    .single();
                if (err || !data) throw err ?? new Error("Failed to create scorecard");
                scorecardId = data.id;
            } else {
                scorecardId = Number(id);
                const { error: err } = await supabase
                    .from("scorecards")
                    .update({ name, description, bonus_cap: bonusCap })
                    .eq("id", scorecardId);
                if (err) throw err;
            }

            // Helper: upsert child table rows
            const upsertRows = async (table: string, rows: any[]) => {
                // Delete rows not present in current state (only for existing scorecards)
                if (!isNew) {
                    const existingIds = rows.filter((r) => r.id !== undefined).map((r) => r.id);
                    // Delete rows not in current set
                    if (existingIds.length > 0) {
                        await supabase
                            .from(table)
                            .delete()
                            .eq("scorecard_id", scorecardId)
                            .not("id", "in", `(${existingIds.join(",")})`);
                    } else {
                        await supabase.from(table).delete().eq("scorecard_id", scorecardId);
                    }
                }

                for (let i = 0; i < rows.length; i++) {
                    const { _localId: _l, id: rowId, ...rest } = rows[i];
                    const payload = { ...rest, scorecard_id: scorecardId, sort_order: i };
                    if (rowId !== undefined) {
                        await supabase.from(table).update(payload).eq("id", rowId);
                    } else {
                        await supabase.from(table).insert(payload);
                    }
                }
            };

            await upsertRows("scorecard_gates", gates);
            await upsertRows("scorecard_criteria", criteria);
            await upsertRows("scorecard_bonuses", bonuses);
            await upsertRows("scorecard_tier_labels", tierLabels);

            navigate("/scorecards");
        } catch (e: any) {
            setError(e?.message ?? "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const addGate = () =>
        setGates((prev) => [
            ...prev,
            {
                _localId: uid(),
                code: "",
                label: "",
                field_reference: "",
                operator: "eq",
                threshold_value: "",
                sort_order: prev.length,
            },
        ]);

    const addCriterion = () =>
        setCriteria((prev) => [
            ...prev,
            {
                _localId: uid(),
                code: "",
                label: "",
                field_reference: "",
                max_points: 10,
                tiers: [],
                sort_order: prev.length,
            },
        ]);

    const addBonus = () =>
        setBonuses((prev) => [
            ...prev,
            {
                _localId: uid(),
                code: "",
                label: "",
                field_reference: "",
                max_points: 5,
                tiers: [],
                sort_order: prev.length,
            },
        ]);

    const addTierLabel = () =>
        setTierLabels((prev) => [
            ...prev,
            {
                _localId: uid(),
                name: "",
                min_score: 0,
                max_score: 100,
                color: "#6b7280",
                description: "",
                sort_order: prev.length,
            },
        ]);

    if (loading) {
        return <div className="p-6 text-muted-foreground">Loading scorecard…</div>;
    }

    return (
        <div className="mx-auto max-w-5xl p-6">
            {/* Header */}
            <div className="mb-6 flex items-center justify-between">
                <h1 className="text-2xl font-bold">
                    {isNew ? "New Scorecard" : "Edit Scorecard"}
                </h1>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => navigate("/scorecards")}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={saving}>
                        {saving ? "Saving…" : "Save"}
                    </Button>
                </div>
            </div>

            {error && (
                <div className="mb-4 rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
                    {error}
                </div>
            )}

            {/* Scorecard meta fields */}
            <div className="mb-6 grid grid-cols-[1fr_1fr_120px] gap-4">
                <div>
                    <label className="block text-sm font-medium mb-1">Name</label>
                    <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="MYLES v1"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <Input
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Optional description"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">Bonus Cap</label>
                    <Input
                        type="number"
                        value={bonusCap}
                        onChange={(e) => setBonusCap(Number(e.target.value))}
                    />
                </div>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="gates">
                <TabsList>
                    <TabsTrigger value="gates">Gates ({gates.length})</TabsTrigger>
                    <TabsTrigger value="criteria">Criteria ({criteria.length})</TabsTrigger>
                    <TabsTrigger value="bonus">Bonus ({bonuses.length})</TabsTrigger>
                    <TabsTrigger value="tiers">Tier Labels ({tierLabels.length})</TabsTrigger>
                </TabsList>

                {/* Gates */}
                <TabsContent value="gates">
                    <div className="mt-4 rounded-md border p-4">
                        <p className="text-xs text-muted-foreground mb-3">
                            Gates are mandatory pass/fail checks. A contact that fails any gate is
                            immediately disqualified (score = 0, tier = Disqualified).
                        </p>
                        {gates.map((gate) => (
                            <GateRow
                                key={gate._localId}
                                gate={gate}
                                onChange={(updated) =>
                                    setGates((prev) =>
                                        prev.map((g) => (g._localId === gate._localId ? updated : g)),
                                    )
                                }
                                onDelete={() =>
                                    setGates((prev) => prev.filter((g) => g._localId !== gate._localId))
                                }
                            />
                        ))}
                        <Button variant="outline" size="sm" className="mt-3" onClick={addGate}>
                            + Add Gate
                        </Button>
                    </div>
                </TabsContent>

                {/* Criteria */}
                <TabsContent value="criteria">
                    <div className="mt-4 rounded-md border p-4">
                        <p className="text-xs text-muted-foreground mb-3">
                            Scored criteria contribute points toward the total score. Define tiers to
                            map field values to point values.
                        </p>
                        {criteria.map((item) => (
                            <CriterionRow
                                key={item._localId}
                                item={item}
                                onChange={(updated) =>
                                    setCriteria((prev) =>
                                        prev.map((c) => (c._localId === item._localId ? (updated as LocalCriterion) : c)),
                                    )
                                }
                                onDelete={() =>
                                    setCriteria((prev) =>
                                        prev.filter((c) => c._localId !== item._localId),
                                    )
                                }
                            />
                        ))}
                        <Button variant="outline" size="sm" className="mt-3" onClick={addCriterion}>
                            + Add Criterion
                        </Button>
                    </div>
                </TabsContent>

                {/* Bonus */}
                <TabsContent value="bonus">
                    <div className="mt-4 rounded-md border p-4">
                        <p className="text-xs text-muted-foreground mb-3">
                            Bonus criteria add extra points up to the Bonus Cap ({bonusCap} pts).
                        </p>
                        {bonuses.map((item) => (
                            <CriterionRow
                                key={item._localId}
                                item={item}
                                onChange={(updated) =>
                                    setBonuses((prev) =>
                                        prev.map((b) => (b._localId === item._localId ? (updated as LocalBonus) : b)),
                                    )
                                }
                                onDelete={() =>
                                    setBonuses((prev) =>
                                        prev.filter((b) => b._localId !== item._localId),
                                    )
                                }
                            />
                        ))}
                        <Button variant="outline" size="sm" className="mt-3" onClick={addBonus}>
                            + Add Bonus
                        </Button>
                    </div>
                </TabsContent>

                {/* Tier Labels */}
                <TabsContent value="tiers">
                    <div className="mt-4 rounded-md border p-4">
                        <p className="text-xs text-muted-foreground mb-3">
                            Tier labels map score ranges to named tiers (e.g. "Angel Target" for
                            85–100 pts).
                        </p>
                        {tierLabels.map((item) => (
                            <TierLabelRow
                                key={item._localId}
                                item={item}
                                onChange={(updated) =>
                                    setTierLabels((prev) =>
                                        prev.map((t) => (t._localId === item._localId ? updated : t)),
                                    )
                                }
                                onDelete={() =>
                                    setTierLabels((prev) =>
                                        prev.filter((t) => t._localId !== item._localId),
                                    )
                                }
                            />
                        ))}
                        <Button variant="outline" size="sm" className="mt-3" onClick={addTierLabel}>
                            + Add Tier Label
                        </Button>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
